import React, { useState, useEffect } from "react"

import { useStaticQuery, graphql, Link } from "gatsby"
import PropTypes from "prop-types"

import * as styles from "./header.module.scss"
import { isLoggedIn, getUser } from "../../../helpers/auth"
import UrlFunctions from "../../../helpers/url"
import Button from "../../atoms/button"
import Icon from "../../atoms/icon"
import Logo from "../../atoms/logo"
import Overlay from "../../atoms/overlay"
import Wrapper from "../../atoms/wrapper"
import Filters from "../../molecules/filters"
import Nav from "../../molecules/nav"
import Geolocation from "../geolocation"
import SubscribePrompt from "../subscribe-prompt"

const Header = ({
  pageType,
  currentSport,
  currentVertical,
  customHeader,
  filters,
}) => {
  const { allSanityDictionary, sanityNavigation, allSanitySubscribe } =
    useStaticQuery(graphql`
      {
        allSanityDictionary {
          nodes {
            key {
              current
            }
            value
          }
        }
        allSanitySubscribe {
          nodes {
            vertical {
              slug {
                current
              }
            }
          }
        }
        sanityNavigation(type: { eq: "header" }) {
          items {
            title
            type
            url
            submenu {
              items {
                url
                title
                type
                email_subject
                email_body
                email_address
              }
            }
            email_body
            email_address
            email_subject
          }
        }
      }
    `)
  const [hamburgerOpen, setHamburgerOpen] = useState(false)
  const [user, setUser] = useState()
  const nav = customHeader?.navigation?.items || sanityNavigation.items
  const isSubscribe = !!allSanitySubscribe.nodes.find(
    (subscribe) => subscribe.vertical.slug.current === currentVertical
  )

  const toggleHamburger = () => {
    setHamburgerOpen(!hamburgerOpen)
  }

  useEffect(() => {
    if (hamburgerOpen) {
      document.body.classList.add("disableScroll")
    } else {
      document.body.classList.remove("disableScroll")
    }
    if (isLoggedIn())
      getUser((data) => {
        setUser(data?.first_name || data?.email || "User")
      })
  })

  useEffect(() => {
    const growthChannel =
      UrlFunctions.getParams().sqf_channel || UrlFunctions.getParams().utm_id
    const { growthchanneldetails } = UrlFunctions.getParams()
    if (
      pageType === "article" &&
      !localStorage.getItem(`subscribed_${currentVertical}`)
    ) {
      localStorage.setItem("growth_channel", "organic-website")
      localStorage.setItem("growthchanneldetails", "gating")
    }
    if (growthChannel) {
      localStorage.setItem("growth_channel", growthChannel)
      if (growthchanneldetails) {
        localStorage.setItem("growthchanneldetails", growthchanneldetails)
      } else {
        localStorage.removeItem("growthchanneldetails")
      }
    }
  }, [])

  const skip = (e) => {
    e.preventDefault()
    document.getElementById("#main").focus()
  }

  const listen = allSanityDictionary.nodes.find(
    (item) => item.key.current === "listen"
  )?.value

  const subscribe = allSanityDictionary.nodes.find(
    (item) => item.key.current === "subscribe"
  )?.value

  const customCta = customHeader?.ctas?.find(
    (h) => !h.country || h.country.abbreviation === process.env.GATSBY_COUNTRY
  )

  if (!nav.find((item) => item.title === listen) && !customCta) {
    nav.push({
      url: "/podcasts",
      title: listen,
      showMobile: true,
      type: "url",
    })
  }

  if (customHeader?.navigation?.items) {
    if (customCta && !nav.find((item) => item.title === customCta.text)) {
      nav.push({
        url: customCta.url,
        title: customCta.text,
        showMobile: true,
        type: "url",
      })
    }
  }
  if (
    customHeader?.partner?.logo &&
    !nav.find((item) => item.title === subscribe)
  ) {
    nav.push({
      url: "/subscribe",
      title: subscribe,
      showMobile: true,
      type: "url",
    })
  }

  return (
    <>
      <Button pressEvent={skip} className={styles.skipToContent}>
        {
          allSanityDictionary.nodes.find(
            (item) => item.key.current === "skip_to_content"
          )?.value
        }
      </Button>
      <Geolocation custom={!!customHeader} />
      <SubscribePrompt pageType={pageType} custom={!!customHeader} />
      <header
        className={`${styles.header} ${!filters ? styles.header__custom : ""}`}
      >
        <div className={styles.account}>
          <Wrapper>
            <Icon icon="person" light />
            {user ? (
              <Link className={styles.accountLink} to="/account/profile">
                {
                  allSanityDictionary.nodes.find(
                    (item) => item.key.current === "welcome"
                  )?.value
                }{" "}
                {user} ·{" "}
                {
                  allSanityDictionary.nodes.find(
                    (item) => item.key.current === "profile_anonymous"
                  )?.value
                }
              </Link>
            ) : (
              <Link className={styles.accountLink} to="/account/login">
                {
                  allSanityDictionary.nodes.find(
                    (item) => item.key.current === "login"
                  )?.value
                }
              </Link>
            )}
          </Wrapper>
        </div>
        <Wrapper flex className={styles.wrapper}>
          <Logo />
          {customHeader?.partner?.logo ? (
            <Logo
              logo={customHeader.partner.logo}
              alt={customHeader.partner.name}
              url={customHeader.url || customHeader.partner.url}
              small
            />
          ) : null}
          <div
            className={`${styles.content} ${
              hamburgerOpen ? styles.content__open : ""
            } ${customHeader ? styles.content__custom : ""}`}
          >
            <Nav nav={nav} />
          </div>
          {customCta ? (
            <Button
              className={`${styles.button__hidden} ${styles.button}`}
              url={customCta.url}
              variant={customCta.variant}
            >
              {customCta.text}
            </Button>
          ) : (
            <>
              <Button
                className={`${styles.button__hidden} ${styles.button}`}
                variant="secondary"
                url="/podcasts"
              >
                {listen}
              </Button>
              <Button
                className={`${styles.button} ${
                  customHeader?.partner?.logo ? styles.button__hidden : ""
                }`}
                url={`/subscribe${
                  currentVertical && isSubscribe ? `-${currentVertical}` : ""
                }`}
              >
                {subscribe}
              </Button>
            </>
          )}
          <div
            role="button"
            className={`${styles.hamburger} ${
              hamburgerOpen ? styles.hamburger__open : ""
            }`}
            onClick={toggleHamburger}
            onKeyPress={toggleHamburger}
            aria-label="Menu"
            tabIndex="0"
          >
            <span />
            <span />
            <span />
            <span />
          </div>
        </Wrapper>
        {!filters ? (
          ""
        ) : (
          <Filters
            currentSport={currentSport}
            currentVertical={currentVertical}
          />
        )}
      </header>
      <Overlay
        label="Menu"
        menu
        trigger={hamburgerOpen}
        event={toggleHamburger}
        custom={!!customHeader}
      />
    </>
  )
}

export default Header

Header.propTypes = {
  pageType: PropTypes.string.isRequired,
  currentSport: PropTypes.string,
  currentVertical: PropTypes.string,
  customHeader: PropTypes.object,
  filters: PropTypes.bool,
}

Header.defaultProps = {
  currentSport: "",
  currentVertical: "",
  customHeader: null,
  filters: true,
}
